// Layer: infrastructure. Implements the StoriaApi port over HTTP.
//
// Every request carries a bearer credential and a correlation id. The API derives the
// tenant from that credential, so nothing here sends an operator id. A console that
// could name its own tenant would make the server's isolation checks meaningless.
//
// Reads retry through a cold start; writes do not. See `request` for why.
import {
  COLD_START_BUDGET_MS,
  WAKING_AFTER_MS,
  isWaking,
  nextAttempt,
} from "../application/cold-start";
import type { ApiActivity, ApiPhase, StoriaApi, TokenSource } from "../application/ports";
import {
  ApiError,
  type Action,
  type ApiFailure,
  type AuditFeed,
  type Decision,
  type GroupKpis,
  type GuestJourney,
  type GuestSummary,
  type MergeCandidate,
  type Playbook,
  type ShiftView,
} from "../domain/types";

/** The window a single attempt gets when it is not being retried, which means a write.
 *  Set to the whole cold-start budget: a write issued while the API is still starting
 *  has to wait it out, because the console must not send it twice to shorten the wait. */
const WRITE_TIMEOUT_MS = COLD_START_BUDGET_MS;

export interface HttpApiOptions {
  baseUrl: string;
  tokens: TokenSource;
  /** Pins every attempt to one window, overriding the plan. Tests only. */
  timeoutMs?: number;
  fetchImpl?: typeof fetch;
  /** Injected so tests do not sleep. Defaults to setTimeout. */
  sleepImpl?: (ms: number) => Promise<void>;
  nowImpl?: () => number;
  /** Runs `fn` after `ms`; the returned function cancels it. Defaults to setTimeout.
   *  Separate from `sleepImpl` because this one has to fire while a request the caller
   *  is still awaiting is in flight, which a sleep the caller awaits cannot do. */
  scheduleImpl?: (fn: () => void, ms: number) => () => void;
}

export class HttpStoriaApi implements StoriaApi, ApiActivity {
  private readonly baseUrl: string;
  private readonly tokens: TokenSource;
  private readonly fixedTimeoutMs: number | undefined;
  private readonly fetchImpl: typeof fetch;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly now: () => number;
  private readonly schedule: (fn: () => void, ms: number) => () => void;
  private readonly listeners = new Set<(phase: ApiPhase) => void>();
  private phase: ApiPhase = "idle";
  /** Reads in flight that have declared themselves slow. The banner stays while any of
   *  them is still waiting, so when two panels load at once the first to finish cannot
   *  clear the banner the second still needs. */
  private waking = 0;

  constructor(options: HttpApiOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.tokens = options.tokens;
    this.fixedTimeoutMs = options.timeoutMs;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.sleep =
      options.sleepImpl ??
      ((ms) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
    this.now = options.nowImpl ?? (() => Date.now());
    this.schedule =
      options.scheduleImpl ??
      ((fn, ms) => {
        const id = setTimeout(fn, ms);
        return () => clearTimeout(id);
      });
  }

  subscribe(listener: (phase: ApiPhase) => void): () => void {
    this.listeners.add(listener);
    listener(this.phase);
    return () => {
      this.listeners.delete(listener);
    };
  }

  async loadShift(propertyId: string): Promise<ShiftView> {
    return this.read<ShiftView>(`/v1/properties/${encodeURIComponent(propertyId)}/shift`);
  }

  async loadPlaybooks(propertyId: string): Promise<Playbook[]> {
    const body = await this.read<{ playbooks: Playbook[] }>(
      `/v1/properties/${encodeURIComponent(propertyId)}/playbooks`,
    );
    return body.playbooks;
  }

  async decide(actionId: string, decision: Decision): Promise<Action> {
    return this.write<Action>(`/v1/actions/${encodeURIComponent(actionId)}:decide`, {
      decision,
      correlation_id: correlationId(),
    });
  }

  async loadAudit(limit: number): Promise<AuditFeed> {
    return this.read<AuditFeed>(`/v1/audit?limit=${limit}`);
  }

  async loadGroupKpis(propertyIds: string[], windowDays: number): Promise<GroupKpis> {
    // A POST that reads. Safe to retry, because it has no effect on the server, so it
    // takes the read path despite the verb.
    return this.request<GroupKpis>(
      "POST",
      "/v1/kpis:group",
      { property_ids: propertyIds, window_days: windowDays },
      { retry: true },
    );
  }

  async loadGuests(): Promise<GuestSummary[]> {
    const body = await this.read<{ guests: GuestSummary[] }>("/v1/guests");
    return body.guests;
  }

  async loadGuestJourney(guestId: string): Promise<GuestJourney> {
    return this.read<GuestJourney>(`/v1/guests/${encodeURIComponent(guestId)}/journey`);
  }

  async loadMergeCandidates(): Promise<MergeCandidate[]> {
    const body = await this.read<{ candidates: MergeCandidate[] }>(
      "/v1/identity/merge-candidates",
    );
    return body.candidates;
  }

  async resolveMerge(
    candidateId: number,
    merge: boolean,
    keepId?: string,
  ): Promise<void> {
    await this.write<unknown>(
      `/v1/identity/merge-candidates/${candidateId}:resolve`,
      { merge, keep_id: keepId ?? null, correlation_id: correlationId() },
    );
  }

  async savePlaybook(propertyId: string, playbook: Playbook): Promise<void> {
    // Built field by field rather than spread from the loaded playbook. `PlaybookIn`
    // is `extra="forbid"`, and a read carries two fields it refuses: `version`, which
    // the store assigns, and `required_facts`, which it derives. Sending the graph as
    // it arrived is a 422 on all four counts at once. Naming the fields means a new
    // one on the read cannot leak into the write by accident.
    await this.write<unknown>("/v1/playbooks", {
      id: playbook.id,
      name: playbook.name,
      property_id: propertyId,
      nodes: playbook.nodes,
      correlation_id: correlationId(),
    });
  }

  private async read<T>(path: string): Promise<T> {
    return this.request<T>("GET", path, undefined, { retry: true });
  }

  private async write<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>("POST", path, body, { retry: false });
  }

  /**
   * One logical call, retried through a cold start when that is safe.
   *
   * Only reads retry. A write that times out may well have been applied: Cloud Run
   * holds the request while the container starts, so a client-side abort says nothing
   * about what the server did with it. Retrying `savePlaybook` would store a second
   * version of the graph, and retrying `resolveMerge` would resolve a candidate the
   * first attempt already resolved. A duplicate guest merge is a personal-data
   * incident, which is a far worse outcome than a wait. So writes get one attempt with
   * the whole budget as its window, and sit the cold start out rather than racing it.
   */
  private async request<T>(
    method: "GET" | "POST",
    path: string,
    body: unknown,
    options: { retry: boolean },
  ): Promise<T> {
    if (!options.retry) {
      return this.attempt<T>(method, path, body, this.fixedTimeoutMs ?? WRITE_TIMEOUT_MS);
    }

    const started = this.now();
    let declaredWaking = false;
    let attemptNumber = 1;
    let last: ApiError | null = null;

    // Armed when the first attempt starts, and NOT from the failure path.
    //
    // This used to live in the catch branch below, which made the banner a side effect
    // of the first attempt's 10s timeout: it could only appear once a request had
    // failed. A cold start that answers in under 10s never fails, so the operator got a
    // bare "Loading" for the whole wait and then the data. Measured against a genuinely
    // cold service (zero instances, first load 11.74s), the banner never appeared at
    // all, which is precisely the unexplained wait it exists to replace.
    //
    // "Retry on failure" and "say we are waiting" are different clocks. This one runs
    // while a request the caller is still awaiting is in flight.
    const cancelArm = this.schedule(() => {
      if (!declaredWaking) {
        declaredWaking = true;
        this.enterWaking();
      }
    }, WAKING_AFTER_MS);

    try {
      for (;;) {
        const plan = nextAttempt(attemptNumber, this.now() - started);
        // Budget spent. `last` is always set here: attempt 1 is unconditionally planned,
        // so the only route to this line is through a failure already recorded.
        if (plan === null) throw last ?? new ApiError(exhausted());
        if (plan.delayMs > 0) await this.sleep(plan.delayMs);

        try {
          return await this.attempt<T>(
            method,
            path,
            body,
            this.fixedTimeoutMs ?? plan.timeoutMs,
          );
        } catch (e) {
          if (!(e instanceof ApiError) || !isWaking(e.failure.kind)) throw e;
          last = e;
          attemptNumber += 1;
        }
      }
    } finally {
      cancelArm();
      if (declaredWaking) this.leaveWaking();
    }
  }

  private async attempt<T>(
    method: "GET" | "POST",
    path: string,
    body: unknown,
    timeoutMs: number,
  ): Promise<T> {
    const token = await this.tokens.get();
    if (!token) throw new ApiError({ kind: "unauthenticated" });

    // Rules §4.4, every external call has a timeout. `fetch` has none of its own, and
    // a Cloud Run instance that never finishes starting would otherwise hang forever.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let response: Response;
    try {
      response = await this.fetchImpl(`${this.baseUrl}${path}`, {
        method,
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Correlation-Id": correlationId(),
          ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch (e) {
      const detail =
        e instanceof DOMException && e.name === "AbortError"
          ? "The request timed out."
          : String(e);
      throw new ApiError({ kind: "unavailable", detail });
    } finally {
      clearTimeout(timer);
    }

    if (!response.ok) {
      if (response.status === 401) this.tokens.clear();
      throw new ApiError(await toFailure(response));
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  private enterWaking(): void {
    this.waking += 1;
    this.publish("waking");
  }

  private leaveWaking(): void {
    this.waking = Math.max(0, this.waking - 1);
    if (this.waking === 0) this.publish("idle");
  }

  private publish(phase: ApiPhase): void {
    if (this.phase === phase) return;
    this.phase = phase;
    for (const listener of this.listeners) listener(phase);
  }
}

/** The failure reported once the whole budget is spent. Says what actually happened. */
function exhausted(): ApiFailure {
  const seconds = Math.round(COLD_START_BUDGET_MS / 1000);
  return { kind: "unavailable", detail: `STORIA did not answer within ${seconds} seconds.` };
}

async function toFailure(response: Response): Promise<ApiFailure> {
  const detail = await readDetail(response);
  switch (response.status) {
    case 401:
      return { kind: "unauthenticated" };
    case 403:
      return { kind: "forbidden", detail };
    case 404:
      return { kind: "not_found" };
    case 409:
      return { kind: "conflict", detail };
    case 413:
    case 422:
      return { kind: "invalid", detail };
    default:
      return { kind: "unavailable", detail: detail || `HTTP ${response.status}` };
  }
}

async function readDetail(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (body && typeof body === "object" && "detail" in body) {
      return renderDetail((body as { detail: unknown }).detail);
    }
    return "";
  } catch {
    return "";
  }
}

/**
 * The API's `detail` as something an operator can read.
 *
 * A 422 from FastAPI carries a LIST of validation errors, not a string, and this was
 * `JSON.stringify`d straight into the alert. A rejected decide therefore showed
 * `[{"type":"uuid_parsing","loc":["path","action_id"],"msg":"..."}]`, which reads as
 * machine noise rather than as "your approval did not happen" — the symptom reported
 * from UAT was that nothing on the screen changed, not that an error appeared.
 *
 * An unrecognised shape returns "" on purpose. `describeFailure` carries a lead sentence
 * of its own, and no detail at all is better than a dump of one.
 */
function renderDetail(detail: unknown): string {
  if (typeof detail === "string") return detail;
  if (!Array.isArray(detail)) return "";
  return detail
    .map(validationMessage)
    .filter((part): part is string => part !== null)
    .join("; ");
}

/** One FastAPI validation error as `<field>: <message>`, or null if it carries neither. */
function validationMessage(item: unknown): string | null {
  if (!item || typeof item !== "object") return null;
  const { msg, loc } = item as { msg?: unknown; loc?: unknown };
  if (typeof msg !== "string" || !msg.trim()) return null;
  // "body" and "path" name where the value came from, not which field it was; the
  // operator needs the field. `loc` is ["path", "action_id"] or ["body", "decision"].
  const field = Array.isArray(loc)
    ? loc.filter((part) => typeof part === "string" && part !== "body" && part !== "path")
        .at(-1)
    : undefined;
  return typeof field === "string" ? `${field}: ${msg}` : msg;
}

function correlationId(): string {
  return `console-${crypto.randomUUID()}`;
}
